import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Mic, MicOff, Volume2, SkipForward,
  CheckCircle, XCircle, Home, RefreshCw
} from "lucide-react";
import { Progress } from "../components/ui/progress";
import { API } from "../utils/api";

// ─── Levenshtein mesafesi ───────────────────────────────────────────────────
const levenshteinDistance = (str1, str2) => {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = str1[i - 1] === str2[j - 1]
        ? dp[i - 1][j - 1]
        : Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]) + 1;
    }
  }
  return dp[m][n];
};

// ─── Türkçe karakter normalize ──────────────────────────────────────────────
const normalizeForComparison = (text) => {
  return text
    .toLowerCase()
    .replace(/ü/g, "u")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/İ/g, "i")
    .replace(/I/g, "i");
};

// ─── Global AudioContext ────────────────────────────────────────────────────
let globalAudioContext = null;

const getAudioContext = () => {
  if (!globalAudioContext || globalAudioContext.state === "closed") {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) globalAudioContext = new AC();
  }
  return globalAudioContext;
};

const unlockAudioContext = async () => {
  const ctx = getAudioContext();
  if (ctx && ctx.state === "suspended") {
    await ctx.resume();
  }
};

// ─── Ses çalma yardımcısı ───────────────────────────────────────────────────
const playSound = (path) => {
  return new Promise(async (resolve, reject) => {
    try {
      const ctx = getAudioContext();
      if (!ctx) {
        const audio = new Audio(path);
        audio.onended = resolve;
        audio.onerror = () => reject(new Error("Audio load failed"));
        audio.play().catch(reject);
        return;
      }
      if (ctx.state === "suspended") await ctx.resume();
      const response = await fetch(path);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);
      source.onended = resolve;
      source.start(0);
    } catch (err) {
      console.warn("playSound hatası, TTS'e düşülüyor:", err);
      reject(err);
    }
  });
};

// ─── Kutlama Efekti Komponenti ───────────────────────────────────────────────
const CelebrationEffect = ({ show }) => {
  if (!show) return null;

  const confettiColors = ["#06D6A0", "#FFD166", "#EF476F", "#4CC9F0", "#9B5DE5", "#FF9F1C"];
  const emojis = ["🌟", "⭐", "🎉", "🎈", "✨", "🏆", "👏", "🎊"];

  const confettiPieces = Array.from({ length: 60 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
    delay: `${Math.random() * 0.8}s`,
    duration: `${1.2 + Math.random() * 1.2}s`,
    size: `${8 + Math.random() * 10}px`,
    rotate: `${Math.random() * 360}deg`,
    shape: Math.random() > 0.5 ? "50%" : "0%",
  }));

  const emojiPieces = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    left: `${10 + Math.random() * 80}%`,
    emoji: emojis[Math.floor(Math.random() * emojis.length)],
    delay: `${Math.random() * 0.6}s`,
    duration: `${1.5 + Math.random() * 1}s`,
    size: `${24 + Math.random() * 24}px`,
  }));

  const stars = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 1}s`,
    duration: `${0.8 + Math.random() * 0.8}s`,
    size: `${12 + Math.random() * 16}px`,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden">
      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(-20px) rotate(0deg) scale(1); opacity: 1; }
          80% { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg) scale(0.5); opacity: 0; }
        }
        @keyframes emojiBounce {
          0% { transform: translateY(0px) scale(0); opacity: 0; }
          20% { transform: translateY(-40px) scale(1.3); opacity: 1; }
          50% { transform: translateY(-80px) scale(1); opacity: 1; }
          100% { transform: translateY(-160px) scale(0.5); opacity: 0; }
        }
        @keyframes starPop {
          0% { transform: translateY(-10px) scale(0) rotate(0deg); opacity: 0; }
          30% { transform: translateY(30px) scale(1.4) rotate(180deg); opacity: 1; }
          100% { transform: translateY(100vh) scale(0.3) rotate(360deg); opacity: 0; }
        }
        @keyframes balloon {
          0% { transform: translateY(110vh) scale(0.5); opacity: 0; }
          20% { opacity: 1; transform: translateY(60vh) scale(1); }
          100% { transform: translateY(-20vh) scale(1.1); opacity: 0.8; }
        }
      `}</style>

      {/* Konfeti */}
      {confettiPieces.map((p) => (
        <div
          key={`c-${p.id}`}
          style={{
            position: "absolute",
            top: "-20px",
            left: p.left,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.shape,
            transform: `rotate(${p.rotate})`,
            animation: `confettiFall ${p.duration} ${p.delay} ease-in forwards`,
          }}
        />
      ))}

      {/* Emojiler */}
      {emojiPieces.map((p) => (
        <div
          key={`e-${p.id}`}
          style={{
            position: "absolute",
            bottom: "20%",
            left: p.left,
            fontSize: p.size,
            animation: `emojiBounce ${p.duration} ${p.delay} ease-out forwards`,
          }}
        >
          {p.emoji}
        </div>
      ))}

      {/* Yıldızlar */}
      {stars.map((p) => (
        <div
          key={`s-${p.id}`}
          style={{
            position: "absolute",
            top: "-10px",
            left: p.left,
            fontSize: p.size,
            animation: `starPop ${p.duration} ${p.delay} ease-in forwards`,
          }}
        >
          ⭐
        </div>
      ))}

      {/* Balonlar */}
      {["🎈", "🎊", "🎈", "🎊", "🎈"].map((b, i) => (
        <div
          key={`b-${i}`}
          style={{
            position: "absolute",
            left: `${15 + i * 18}%`,
            fontSize: `${32 + Math.random() * 20}px`,
            animation: `balloon ${1.8 + i * 0.2}s ${i * 0.15}s ease-out forwards`,
          }}
        >
          {b}
        </div>
      ))}
    </div>
  );
};

// ─── Ana Bileşen ────────────────────────────────────────────────────────────
const ExercisePage = () => {
  const { difficulty } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [exercises, setExercises] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [spokenText, setSpokenText] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionStats, setSessionStats] = useState({ correct: 0, total: 0 });
  const [showCelebration, setShowCelebration] = useState(false); // ✅ Kutlama efekti

  const recognitionRef = useRef(null);
  const isProcessingRef = useRef(false);
  const [showWarning, setShowWarning] = useState(false);

  // ── Egzersizleri çek ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchExercises = async () => {
      try {
        const res = await axios.get(`${API}/exercises?difficulty=${difficulty}`, {
          headers: getAuthHeaders()
        });
        const shuffled = res.data.sort(() => Math.random() - 0.5);
        setExercises(shuffled);
      } catch (error) {
        console.error("Error fetching exercises:", error);
        toast.error("Egzersizler yüklenemedi");
      } finally {
        setLoading(false);
      }
    };
    fetchExercises();
  }, [difficulty, getAuthHeaders]);

  // ── Konuşma tanıma ──────────────────────────────────────────────────────
  const micStreamRef = useRef(null);
  const edgeTimerRef = useRef(null);
  const edgeRetryCountRef = useRef(0);
  const edgeMaxTimeoutRef = useRef(null);
  const retryTimersRef = useRef([]);          // Tüm Edge retry setTimeout id'leri
  const lastInterimResultRef = useRef("");    // Closure yerine ref
  const isEdgeRef = useRef(/Edg\//.test(navigator.userAgent));

  // Tüm zamanlayıcıları temizle — tryAgain / nextExercise tarafından çağrılır
  const clearAllTimers = useCallback(() => {
    if (edgeTimerRef.current) { clearTimeout(edgeTimerRef.current); edgeTimerRef.current = null; }
    if (edgeMaxTimeoutRef.current) { clearTimeout(edgeMaxTimeoutRef.current); edgeMaxTimeoutRef.current = null; }
    retryTimersRef.current.forEach(id => clearTimeout(id));
    retryTimersRef.current = [];
  }, []);

  // Recognition'ı güvenle durdur — tryAgain / nextExercise tarafından çağrılır
  const abortRecognition = useCallback(() => {
    clearAllTimers();
    edgeRetryCountRef.current = 0;
    lastInterimResultRef.current = "";
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (_) { /* ignore */ }
    }
    setIsListening(false);
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }
  }, [clearAllTimers]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const isEdge = isEdgeRef.current;

    const recognition = new SpeechRecognition();

    if (isEdge) {
      // ── EDGE: continuous + interimResults AKTİF ──
      recognition.continuous = true;
      recognition.interimResults = true;
    } else {
      // ── CHROME: standart mod ──
      recognition.continuous = false;
      recognition.interimResults = true;
    }

    recognition.lang = "tr-TR";
    recognition.maxAlternatives = 5;

    recognition.onresult = (event) => {
      // Sonuç geldi — Edge retry sayacını sıfırla
      edgeRetryCountRef.current = 0;
      const results = event.results[event.results.length - 1];

      if (!results.isFinal) {
        lastInterimResultRef.current = results[0].transcript.toLowerCase().trim();
        // Ara sonuç geldiğinde zamanlayıcıyı sıfırla
        if (edgeTimerRef.current) clearTimeout(edgeTimerRef.current);
        edgeTimerRef.current = setTimeout(() => {
          if (lastInterimResultRef.current) {
            setSpokenText(lastInterimResultRef.current);
            setIsListening(false);
            try { recognition.stop(); } catch (_) { /* ignore */ }
          }
        }, 2500);
        return;
      }

      // Final sonuç
      if (edgeTimerRef.current) clearTimeout(edgeTimerRef.current);
      if (edgeMaxTimeoutRef.current) clearTimeout(edgeMaxTimeoutRef.current);

      const alternatives = [];
      for (let i = 0; i < results.length; i++) {
        alternatives.push(results[i].transcript.toLowerCase().trim());
      }
      setSpokenText(alternatives.join("||"));
      setIsListening(false);
      try { recognition.stop(); } catch (_) { /* ignore */ }
    };

    recognition.onerror = (event) => {
      console.error("Speech error:", event.error, isEdge ? `(Edge retry: ${edgeRetryCountRef.current}/3)` : "");
      if (edgeTimerRef.current) clearTimeout(edgeTimerRef.current);

      // Ara sonuç varsa onu kullan
      if (event.error === "no-speech" && lastInterimResultRef.current) {
        if (edgeMaxTimeoutRef.current) clearTimeout(edgeMaxTimeoutRef.current);
        setSpokenText(lastInterimResultRef.current);
        setIsListening(false);
        return;
      }

      // ── EDGE: no-speech sessiz retry (max 3) ──
      if (event.error === "no-speech" && isEdge && edgeRetryCountRef.current < 3) {
        edgeRetryCountRef.current++;
        try { recognition.stop(); } catch (_) { /* ignore */ }
        const tid = setTimeout(() => {
          try {
            lastInterimResultRef.current = "";
            recognition.start();
          } catch (_) {
            setIsListening(false);
          }
        }, 200);
        retryTimersRef.current.push(tid);
        return; // Hata mesajı GÖSTERME — sessizce tekrar dene
      }

      // Tüm denemeler tükendi veya Chrome hatası
      if (edgeMaxTimeoutRef.current) clearTimeout(edgeMaxTimeoutRef.current);
      setIsListening(false);

      if (event.error === "not-allowed") {
        toast.error("Mikrofon izni gerekli! Lütfen tarayıcı ayarlarından izin verin.");
      } else if (event.error === "no-speech") {
        toast.info("Ses algılanamadı. Tekrar deneyin.", { duration: 2000 });
      } else if (event.error === "network") {
        toast.error("Ağ hatası. İnternet bağlantınızı kontrol edin.");
      } else if (event.error === "aborted") {
        // Sessizce geç
      }
    };

    recognition.onend = () => {
      // Edge: sonuç gelmeden bittiyse ve retry hakkı varsa sessizce tekrar başlat
      if (isEdge && !lastInterimResultRef.current && edgeRetryCountRef.current < 3) {
        edgeRetryCountRef.current++;
        const tid = setTimeout(() => {
          try {
            recognition.start();
          } catch (_) {
            setIsListening(false);
          }
        }, 200);
        retryTimersRef.current.push(tid);
        return;
      }

      if (edgeMaxTimeoutRef.current) clearTimeout(edgeMaxTimeoutRef.current);
      if (lastInterimResultRef.current) {
        setSpokenText(lastInterimResultRef.current);
      }
      setIsListening(false);
      lastInterimResultRef.current = "";
    };

    recognitionRef.current = recognition;

    return () => {
      clearAllTimers();
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(t => t.stop());
        micStreamRef.current = null;
      }
      recognition.abort();
      recognitionRef.current = null;
    };
  }, [clearAllTimers]);

  const currentExercise = exercises[currentIndex];

  // ── Kelimeyi seslendir (ElevenLabs + TTS yedek) ─────────────────────────
  const speakWord = useCallback(async () => {
    if (!currentExercise) return;

    const toFileName = (word) =>
      word.toLowerCase()
        .replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ş/g, "s")
        .replace(/ı/g, "i").replace(/ö/g, "o").replace(/ç/g, "c")
        .replace(/İ/g, "i").replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "")
        .replace(/_+$/, "");

    const fileName = toFileName(currentExercise.word);
    const soundPath = `/sounds/${fileName}.mp3`;

    try {
      await playSound(soundPath);
    } catch {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(currentExercise.word);
      utterance.lang = "tr-TR";
      utterance.rate = 0.7;
      utterance.volume = 1;
      window.speechSynthesis.speak(utterance);
    }
  }, [currentExercise]);

  // ── Mikrofon başlat / durdur ─────────────────────────────────────────────
  const startListening = useCallback(async () => {
    if (!recognitionRef.current || isListening || isProcessingRef.current) return;

    await unlockAudioContext();
    window.speechSynthesis.cancel();

    setSpokenText("");
    setFeedback(null);
    lastInterimResultRef.current = "";

    const isEdge = isEdgeRef.current;

    // Edge: Mikrofonu önceden ısıt — recognition başlamadan önce mic aktif olsun
    if (isEdge) {
      try {
        if (micStreamRef.current) {
          micStreamRef.current.getTracks().forEach(t => t.stop());
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
        });
        micStreamRef.current = stream;
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (err) {
        toast.error("Mikrofon erişimi sağlanamadı. Tarayıcı ayarlarından mikrofon iznini kontrol edin.");
        return;
      }
    }

    setIsListening(true);
    edgeRetryCountRef.current = 0; // Retry sayacını sıfırla

    try {
      const SpeechGrammarList = window.SpeechGrammarList || window.webkitSpeechGrammarList;
      if (SpeechGrammarList && currentExercise) {
        const words = exercises.map(e => e.word).join(" | ");
        const grammar = `#JSGF V1.0; grammar words; public <word> = ${words};`;
        const grammarList = new SpeechGrammarList();
        grammarList.addFromString(grammar, 1);
        recognitionRef.current.grammars = grammarList;
      }
    } catch (_) { /* grammar not supported */ }

    try {
      recognitionRef.current.start();

      // Edge: 12 saniye sonra hâlâ dinliyorsa otomatik durdur
      if (isEdge) {
        if (edgeMaxTimeoutRef.current) clearTimeout(edgeMaxTimeoutRef.current);
        edgeMaxTimeoutRef.current = setTimeout(() => {
          try { recognitionRef.current?.stop(); } catch (_) { /* ignore */ }
          setIsListening(false);
          if (micStreamRef.current) {
            micStreamRef.current.getTracks().forEach(t => t.stop());
            micStreamRef.current = null;
          }
        }, 12000);
      }
    } catch (err) {
      recognitionRef.current.abort();
      setTimeout(() => {
        try { recognitionRef.current?.start(); } catch (_) { /* ignore */ }
      }, 300);
    }
  }, [isListening, currentExercise, exercises]);

  const stopListening = useCallback(() => {
    abortRecognition();
  }, [abortRecognition]);

  // ── Cevap analizi ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!spokenText || isProcessingRef.current || !currentExercise) return;

    const runAnalysis = async () => {
      isProcessingRef.current = true;

      window.speechSynthesis.cancel();

      const target = currentExercise.word.toLowerCase().trim();
      const allAlternatives = spokenText.split("||").map(s => s.trim());
      const normalTarget = normalizeForComparison(target);
      const matchingAlt = allAlternatives.find(a =>
        a === target || normalizeForComparison(a) === normalTarget
      );
      const spoken = matchingAlt || allAlternatives[0];

      const normalSpoken = normalizeForComparison(spoken);
      const isCorrect = spoken === target || normalSpoken === normalTarget;

      if (isCorrect) {
        setFeedback({ type: "success", message: "Aferin! Çok güzel söyledin! 🌟" });
        // ✅ Kutlama efektini başlat
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 5500);
        await playSound("/sounds/harika.mp3");
      } else {
        setFeedback({ type: "error", message: "Hadi bir daha dene, yapabilirsin! 💪" });
        await playSound("/sounds/tekrar.mp3");
      }

      // Backend'e kaydet
      try {
        await axios.post(`${API}/attempts`, {
          exercise_id: currentExercise.id,
          spoken_text: spoken,
          is_correct: isCorrect,
          difficulty: difficulty
        }, { headers: getAuthHeaders() });
      } catch (err) {
        console.error("İstatistik kaydedilemedi:", err);
      }

      setSessionStats(prev => ({
        correct: prev.correct + (isCorrect ? 1 : 0),
        total: prev.total + 1
      }));

      isProcessingRef.current = false;
    };

    runAnalysis();
  }, [spokenText, currentExercise]);

  // ── Sonraki egzersiz ─────────────────────────────────────────────────────
  const nextExercise = useCallback(() => {
    // Aktif recognition'ı durdur ve tüm zamanlayıcıları temizle
    abortRecognition();

    setFeedback(null);
    setSpokenText("");
    setShowCelebration(false);
    isProcessingRef.current = false;

    if (currentIndex < exercises.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      toast.success(`Tebrikler! ${sessionStats.correct}/${sessionStats.total} doğru!`);
      navigate("/dashboard");
    }
  }, [currentIndex, exercises.length, sessionStats, navigate, abortRecognition]);

  const tryAgain = useCallback(() => {
    // Aktif recognition'ı durdur ve tüm zamanlayıcıları temizle
    abortRecognition();

    setFeedback(null);
    setSpokenText("");
    setShowCelebration(false);
    isProcessingRef.current = false;
  }, [abortRecognition]);

  // ── Yardımcılar ──────────────────────────────────────────────────────────
  const getDifficultyColor = () => {
    switch (difficulty) {
      case "easy": return "#06D6A0";
      case "medium": return "#FFD166";
      case "hard": return "#EF476F";
      default: return "#4CC9F0";
    }
  };

  const getDifficultyName = () => {
    switch (difficulty) {
      case "easy": return "Kolay";
      case "medium": return "Orta";
      case "hard": return "Zor";
      default: return difficulty;
    }
  };

  // ── Yükleniyor ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F9FF] flex items-center justify-center">
        <div className="animate-bounce-in">
          <div className="w-24 h-24 bg-[#4CC9F0] rounded-full flex items-center justify-center">
            <Mic className="w-12 h-12 text-white" />
          </div>
        </div>
      </div>
    );
  }

  if (exercises.length === 0) {
    return (
      <div className="min-h-screen bg-[#F0F9FF] flex flex-col items-center justify-center p-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-[#FFD166]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">😕</span>
          </div>
          <h2 className="text-2xl font-bold text-[#073B4C] mb-4">Bu seviyede egzersiz bulunamadı</h2>
          <Link to="/dashboard" className="btn-primary">Ana Sayfaya Dön</Link>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F0F9FF] flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm px-4 py-4 md:px-8">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/dashboard"
              data-testid="back-btn"
              className="w-12 h-12 bg-[#F0F9FF] rounded-xl flex items-center justify-center hover:bg-[#E0F7FA] transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-[#073B4C]" />
            </Link>
            <div>
              <span
                className="px-3 py-1 rounded-full text-sm font-bold text-white"
                style={{ backgroundColor: getDifficultyColor() }}
              >
                {getDifficultyName()}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <span className="text-[#073B4C] font-bold">
              {currentIndex + 1} / {exercises.length}
            </span>
            <Link
              to="/dashboard"
              data-testid="home-btn"
              className="w-10 h-10 bg-[#F0F9FF] rounded-xl flex items-center justify-center hover:bg-[#E0F7FA] transition-colors"
            >
              <Home className="w-5 h-5 text-[#073B4C]" />
            </Link>
          </div>
        </div>

        {/* Progress bar */}
        <div className="max-w-4xl mx-auto mt-4">
          <Progress
            value={((currentIndex + 1) / exercises.length) * 100}
            className="h-3"
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-4 md:p-8">
        <div className="max-w-lg w-full">
          {/* Kelime Kartı */}
          <div
            key={currentExercise?.id}
            className="bg-white rounded-3xl shadow-lg p-4 mb-8 border-8 animate-bounce-in"
            style={{ borderColor: getDifficultyColor() }}
          >
            <div className="aspect-square rounded-2xl overflow-hidden mb-4">
              <img
                src={currentExercise?.image_url}
                alt={currentExercise?.word}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="text-center">
              <h2
                className="text-4xl md:text-5xl font-black text-[#073B4C] font-['Nunito'] mb-2"
                data-testid="current-word"
              >
                {currentExercise?.word}
              </h2>
              {currentExercise?.pronunciation_hint && (
                <p className="text-lg text-[#8D99AE]">
                  ({currentExercise.pronunciation_hint})
                </p>
              )}
            </div>
          </div>

          {/* Söylenen metin */}
          {spokenText && (
            <div className="bg-white rounded-2xl p-4 mb-6 text-center shadow-md">
              <p className="text-sm text-[#8D99AE] mb-1">Sen söyledin:</p>
              <p className="text-2xl font-bold text-[#073B4C]">{spokenText.split("||")[0]}</p>
            </div>
          )}

          {/* Kontroller */}
          <div className="flex justify-center gap-6 mb-8">
            {/* Dinle */}
            <button
              onClick={speakWord}
              data-testid="listen-btn"
              className="w-20 h-20 bg-[#FFD166] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
              title="Dinle"
            >
              <Volume2 className="w-10 h-10 text-[#073B4C]" />
            </button>

            {/* Mikrofon */}
            <button
              onClick={isListening ? stopListening : startListening}
              data-testid="mic-btn"
              className={`mic-button ${isListening ? "listening" : ""}`}
              title={isListening ? "Dinleniyor..." : "Konuş"}
            >
              {isListening
                ? <MicOff className="w-16 h-16 text-white" />
                : <Mic className="w-16 h-16 text-white" />
              }
            </button>

            {/* Atla */}
            <button
              onClick={nextExercise}
              data-testid="skip-btn"
              className="w-20 h-20 bg-[#4CC9F0] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
              title="Atla"
            >
              <SkipForward className="w-10 h-10 text-[#073B4C]" />
            </button>
          </div>

          {/* Dinleniyor göstergesi */}
          {isListening && (
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-2 bg-[#06D6A0] text-white px-6 py-3 rounded-full animate-pulse">
                <div className="w-3 h-3 bg-white rounded-full animate-bounce" />
                <span className="font-bold">Dinliyorum...</span>
              </div>
            </div>
          )}

          {/* Tarayıcı desteği uyarısı */}
          {!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window) && (
            <div className="bg-[#EF476F]/10 border-2 border-[#EF476F] rounded-2xl p-4 text-center">
              <p className="text-[#EF476F] font-medium">
                Tarayıcınız ses tanımayı desteklemiyor. Lütfen Chrome veya Edge kullanın.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* İlk açılış uyarı popup'ı */}
      {showWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl">
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-[#FFD166]/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-4xl">⚠️</span>
              </div>
              <h3 className="text-xl font-black text-[#073B4C] mb-2">Bilgilendirme</h3>
              <p className="text-[#8D99AE] text-sm leading-relaxed">
                Bu uygulama ses tanıma için Google&apos;ın teknolojisini kullanmaktadır.
                Bazı benzer sesler (örn. <strong className="text-[#073B4C]">t/k, p/b</strong>)
                otomatik olarak düzeltilebilir ve yanlış telaffuz doğru kabul edilebilir.
                <br /><br />
                Bu nedenle <strong className="text-[#073B4C]">terapist değerlendirmesi</strong> her zaman gereklidir.
              </p>
            </div>
            <button
              onClick={() => setShowWarning(false)}
              className="w-full bg-[#06D6A0] text-white font-bold py-3 rounded-2xl hover:bg-[#05c090] transition-colors"
            >
              Anladım, Devam Et
            </button>
          </div>
        </div>
      )}

      {/* Geri Bildirim Modalı */}
      {feedback && (
        <div className="feedback-overlay" data-testid="feedback-modal">
          <div className={`feedback-modal ${feedback.type}`}>
            {feedback.type === "success" ? (
              <CheckCircle className="w-24 h-24 text-[#06D6A0]" />
            ) : feedback.type === "close" ? (
              <div className="w-24 h-24 bg-[#FFD166] rounded-full flex items-center justify-center">
                <span className="text-5xl">💪</span>
              </div>
            ) : (
              <XCircle className="w-24 h-24 text-[#EF476F]" />
            )}

            <h3 className="text-2xl font-black text-[#073B4C] text-center">
              {feedback.message}
            </h3>

            {feedback.type === "error" && (
              <p className="text-lg text-[#8D99AE]">
                Doğrusu: <strong className="text-[#073B4C]">{currentExercise?.word}</strong>
              </p>
            )}
            {feedback.type === "close" && (
              <p className="text-lg text-[#8D99AE]">
                Hedef kelime: <strong className="text-[#073B4C]">{currentExercise?.word}</strong>
              </p>
            )}

            <div className="flex gap-4 mt-4">
              {(feedback.type === "error" || feedback.type === "close") && (
                <button
                  onClick={tryAgain}
                  data-testid="try-again-btn"
                  className="btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  Tekrar Dene
                </button>
              )}
              <button
                onClick={nextExercise}
                data-testid="next-btn"
                className="btn-primary flex items-center gap-2"
              >
                {currentIndex < exercises.length - 1 ? (
                  <>
                    <SkipForward className="w-5 h-5" />
                    <span>Sonraki Kelime</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Bitir</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Kutlama Efekti */}
      <CelebrationEffect show={showCelebration} />

      {/* Footer — Seans istatistikleri */}
      <footer className="bg-white px-4 py-4 border-t border-[#E0F7FA]">
        <div className="max-w-4xl mx-auto flex justify-center gap-8">
          <div className="text-center">
            <p className="text-sm text-[#8D99AE]">Doğru</p>
            <p className="text-2xl font-black text-[#06D6A0]">{sessionStats.correct}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-[#8D99AE]">Toplam</p>
            <p className="text-2xl font-black text-[#073B4C]">{sessionStats.total}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-[#8D99AE]">Başarı</p>
            <p className="text-2xl font-black text-[#FFD166]">
              %{sessionStats.total > 0
                ? Math.round((sessionStats.correct / sessionStats.total) * 100)
                : 0}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ExercisePage;